import { ReactNode, useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import NotificationsDrawer from "./NotificationsDrawer";
import { getUnseenNotifications } from "../../services/notifications";
import { navItems } from "../../nav/navItems";
import { Button } from "./Button";
import { SideNav } from "./SideNav";
import type { NavGroup } from "./SideNav";
import { Navbar } from "./Navbar";
import type { BreadcrumbItem } from "./Breadcrumb";

const pageTitles: Record<string, string> = {
  "/my-consents": "My Consents",
  "/complaints": "Requests & Complaints",
  "/rights": "Rights & FAQs",
};

interface MobileHeaderNested {
  title: string;
  onBack: () => void;
}

function getNestedMobileHeader(
  pathname: string,
  state: { companyName?: string; consentName?: string },
  navigate: ReturnType<typeof useNavigate>,
): MobileHeaderNested | null {
  // /consents/:companyId/requests — reached only via in-page buttons, not the sidebar
  const requestsMatch = pathname.match(/^\/consents\/([^/]+)\/requests$/);
  if (requestsMatch) {
    const companyId = requestsMatch[1];
    return {
      title: "Requests",
      onBack: () => navigate(`/consents/${companyId}`, { state }),
    };
  }

  // /consents/:companyId/:consentId — a single consent notice's detail
  // (excludes my-data/requests, which are handled above or as top-level pages)
  const consentDetailMatch = pathname.match(
    /^\/consents\/([^/]+)\/(?!my-data$|requests$)([^/]+)$/,
  );
  if (consentDetailMatch) {
    const companyId = consentDetailMatch[1];
    return {
      title: state.consentName || "Consent",
      onBack: () => navigate(`/consents/${companyId}`, { state }),
    };
  }

  return null;
}

function getTopLevelMobileTitle(pathname: string): string {
  if (pageTitles[pathname]) return pageTitles[pathname];
  // /consents/:companyId — the consent-notices list for a company
  if (/^\/consents\/[^/]+$/.test(pathname)) return "My Consents";
  // /consents/:companyId/my-data — reached from the sidebar's "My Data" item
  if (/^\/consents\/[^/]+\/my-data$/.test(pathname)) return "My Data";
  return "";
}

function MobileHeader({
  onMenuClick,
  onNotificationsClick,
  unseenCount,
}: {
  onMenuClick: () => void;
  onNotificationsClick: () => void;
  unseenCount: number;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const nested = getNestedMobileHeader(
    location.pathname,
    location.state || {},
    navigate,
  );
  const title = nested?.title ?? getTopLevelMobileTitle(location.pathname);

  return (
    <header className="flex items-center justify-between px-4 py-3 border-b border-gray-200 shrink-0 md:hidden">
      {nested ? (
        <Button variant="tertiary" onClick={nested.onBack} className="!p-1 -ml-1" aria-label="Back">
          <img src="/assets/back-chevron.svg" alt="" width={22} height={22} />
        </Button>
      ) : (
        <Button variant="tertiary" onClick={onMenuClick} className="!p-1 -ml-1" aria-label="Menu">
          <img src="/assets/menu.svg" alt="" width={18} height={18} />
        </Button>
      )}
      <span className="text-base font-semibold text-gray-800 truncate px-2">
        {title}
      </span>
      <Button
        variant="tertiary"
        onClick={onNotificationsClick}
        className="relative !p-1 -mr-1 shrink-0"
        aria-label="Notifications"
      >
        <img src="/assets/bell-gray.svg" alt="" width={18} height={18} />
        {unseenCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-medium leading-none">
            {unseenCount > 9 ? "9+" : unseenCount}
          </span>
        )}
      </Button>
    </header>
  );
}

// Builds the desktop Navbar's breadcrumb trail per route. A single crumb
// renders as a plain title; two or more trigger Navbar's back-button trail
// (back always returns to the first crumb, e.g. "Consents").
function getBreadcrumbs(
  pathname: string,
  state: { companyName?: string; consentName?: string },
): BreadcrumbItem[] {
  if (pageTitles[pathname]) return [{ label: pageTitles[pathname] }];

  // /consents/:companyId — the consent-notices list for a company
  if (/^\/consents\/[^/]+$/.test(pathname)) {
    return [{ label: "My Consents" }];
  }

  // /consents/:companyId/my-data — reached from the sidebar's "My Data" item
  if (/^\/consents\/[^/]+\/my-data$/.test(pathname)) {
    return [{ label: "My Data" }];
  }

  // /consents/:companyId/requests
  const companySubMatch = pathname.match(/^\/consents\/([^/]+)\/requests$/);
  if (companySubMatch) {
    const companyId = companySubMatch[1];
    return [
      { label: "Consents", path: "/my-consents" },
      { label: state.companyName || "Company", path: `/consents/${companyId}` },
    ];
  }

  // /consents/:companyId/:consentId — a single consent notice's detail
  const consentDetailMatch = pathname.match(/^\/consents\/([^/]+)\/([^/]+)$/);
  if (consentDetailMatch) {
    const companyId = consentDetailMatch[1];
    return [
      { label: "Consents", path: "/my-consents" },
      { label: state.companyName || "Company", path: `/consents/${companyId}` },
      { label: state.consentName || "Consent" },
    ];
  }

  return [];
}

const deriveNameFromEmail = (email?: string) => {
  if (!email) return "";
  const local = email.split("@")[0];
  return local
    .split(/[._-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

export default function Layout({ children = null }: { children?: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showNotifications, setShowNotifications] = useState(false);
  const [unseenCount, setUnseenCount] = useState(0);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const tenantId = localStorage.getItem("tenant_id") || "";
  // Both set once at login time (SelectCompany) — trusted for the rest of
  // the session so we don't re-hit /user/tenant-consents on every page load.
  const tenantName = localStorage.getItem("tenant_name") || "";
  const hasMultipleTenants = localStorage.getItem("has_multiple_tenants") === "true";

  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;
    getUnseenNotifications(tenantId)
      .then((res) => {
        if (cancelled) return;
        const list =
          res.data?.items || res.data?.notifications || res.data || [];
        setUnseenCount(Array.isArray(list) ? list.length : 0);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  const user: { name?: string; email?: string } = (() => {
    try {
      const raw = localStorage.getItem("user");
      return (raw ? JSON.parse(raw) : {}) || {};
    } catch {
      return {};
    }
  })();

  const displayName = user.name || deriveNameFromEmail(user.email) || "User";

  const initials =
    displayName
      .split(" ")
      .filter(Boolean)
      .map((n: string) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2) || "U";

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("tenant_id");
    localStorage.removeItem("tenant_name");
    navigate("/login");
  };

  // Groups navItems at each dividerBefore boundary, e.g.
  // [[consents, my-data], [requests-complaints, rights]].
  const sideNavGroups: NavGroup[] = navItems.reduce<NavGroup[]>((groups, item) => {
    if (item.dividerBefore || groups.length === 0) groups.push([]);
    groups[groups.length - 1].push({ label: item.label, path: item.getHref(), icon: item.icon });
    return groups;
  }, []);

  const activePath =
    navItems.find((item) => item.isActive(location.pathname))?.getHref() ??
    location.pathname;

  const breadcrumbs = getBreadcrumbs(location.pathname, location.state || {});

  const brandLogo = (
    <div className="leading-none">
      <div className="text-white font-bold text-xl tracking-widest leading-none">NYAI</div>
      <div className="text-gray-400 text-xs mt-0.5">DPDP Guard</div>
    </div>
  );

  const rightContent = (
    <div className="relative">
      <Button
        variant="tertiary"
        onClick={() => setShowNotifications((v) => !v)}
        className="relative !p-0 hover:!bg-transparent"
        aria-label="Notifications"
      >
        <img src="/assets/bell-red.svg" alt="" width={20} height={20} />
        {unseenCount > 0 && (
          <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-medium leading-none">
            {unseenCount > 9 ? "9+" : unseenCount}
          </span>
        )}
      </Button>

      {showNotifications && (
        <NotificationsDrawer
          tenantId={tenantId}
          onClose={() => setShowNotifications(false)}
          onSeen={(count: number) => setUnseenCount(count ?? 0)}
        />
      )}
    </div>
  );

  // Mirrors the original inline sidebar's "Logged in to {tenant}" block —
  // rendered inside SideNav's footerSlot, above the profile footer.
  const sidebarFooterSlot =
    tenantName && (sidebarOpen || hasMultipleTenants) ? (
      <div className="py-3 px-6">
        {sidebarOpen ? (
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="text-gray-400 text-xs">Logged in to</div>
              <div className="text-white text-sm font-semibold truncate">
                {tenantName}
              </div>
            </div>
            {hasMultipleTenants && (
              <Button
                type="button"
                variant="tertiary"
                onClick={() => navigate("/select-company")}
                leftIcon={
                  <img src="/assets/switch-white.svg" alt="" width={14} height={14} />
                }
                className="shrink-0 !p-0 !text-gray-300 hover:!text-white hover:!bg-transparent !text-sm"
              >
                Switch
              </Button>
            )}
          </div>
        ) : (
          <Button
            type="button"
            variant="tertiary"
            onClick={() => navigate("/select-company")}
            className="flex w-full !p-0 hover:!bg-transparent"
            aria-label="Switch company"
          >
            <img src="/assets/switch-white.svg" alt="" width={16} height={16} />
          </Button>
        )}
      </div>
    ) : null;

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Sidebar */}
      <div className="hidden md:flex h-full">
        <SideNav
          items={sideNavGroups}
          activePath={activePath}
          brandLogo={brandLogo}
          footerSlot={sidebarFooterSlot}
          userName={displayName}
          onLogout={handleLogout}
          collapsed={!sidebarOpen}
          onCollapsedChange={(collapsed) => setSidebarOpen(!collapsed)}
        />
      </div>

      {/* Main */}
      <div className="flex flex-col flex-1 overflow-hidden">
        {/* Mobile app bar */}
        <div className="relative">
          <MobileHeader
            onMenuClick={() => setShowMobileSidebar(true)}
            onNotificationsClick={() => setShowNotifications((v) => !v)}
            unseenCount={unseenCount}
          />
          {showNotifications && (
            <div className="md:hidden">
              <NotificationsDrawer
                tenantId={tenantId}
                onClose={() => setShowNotifications(false)}
                onSeen={(count: number) => setUnseenCount(count ?? 0)}
              />
            </div>
          )}
        </div>

        {/* Mobile sidebar drawer */}
        {showMobileSidebar && (
          <div className="fixed inset-0 z-50 md:hidden">
            <div
              className="absolute inset-0 bg-black/40"
              onClick={() => setShowMobileSidebar(false)}
            />
            <div className="absolute left-0 top-0 h-full w-72 bg-white shadow-xl flex flex-col animate-slide-in-left">
              <div className="flex items-center gap-3 p-4 border-b border-gray-100">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-semibold shrink-0"
                  style={{ background: "#2d7a7a" }}
                >
                  {initials}
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-gray-800 truncate">
                    {displayName}
                  </div>
                  {user.email && (
                    <div className="text-xs text-gray-500 truncate">
                      {user.email}
                    </div>
                  )}
                </div>
              </div>

              {tenantName && (
                <div className="flex flex-col justify-between gap-3 mx-4 mt-4 px-3 py-2.5 rounded-lg border border-[#DCE3E6]">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-gray-800 truncate">
                      {tenantName}
                    </div>
                  </div>
                  {hasMultipleTenants && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowMobileSidebar(false);
                        navigate("/select-company");
                      }}
                      leftIcon={
                        <img
                          src="/assets/switch.svg"
                          alt=""
                          width={12}
                          height={12}
                        />
                      }
                      className="shrink-0 !px-2.5 !py-1 !rounded-md !border-[.5px] !text-xs"
                    >
                      Switch
                    </Button>
                  )}
                </div>
              )}

              <nav className="flex-1 py-6 px-3 overflow-y-auto border-t border-gray-100 mt-6">
                {navItems.map(({ key, label, iconDark, getHref, isActive }) => {
                  const active = isActive(location.pathname);
                  return (
                    <NavLink
                      key={key}
                      to={getHref()}
                      onClick={() => setShowMobileSidebar(false)}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm mb-4 ${
                        active ? "font-medium" : "text-gray-700"
                      }`}
                      style={
                        active
                          ? { background: "#F4FAFB", color: "#0F4C5C" }
                          : {}
                      }
                    >
                      <img src={iconDark} alt="" width={18} height={18} className="shrink-0" />
                      <span className="truncate">{label}</span>
                    </NavLink>
                  );
                })}
              </nav>

              <div className="px-4 pb-3">
                <Button
                  variant="outline"
                  onClick={handleLogout}
                  leftIcon={
                    <img
                      src="/assets/logout-red.svg"
                      alt=""
                      width={16}
                      height={16}
                    />
                  }
                  className="w-full !border-[#C73838] !text-[#C73838] hover:!bg-[#C73838]/5"
                >
                  Logout
                </Button>
              </div>

              <div className="flex items-center justify-center gap-2 py-4 text-sm text-black font-serif">
                <span>Powered by</span>
                <img src="/assets/logo.svg" alt="NYAI" width={46} height={12} />
              </div>
            </div>
          </div>
        )}

        {/* Header — breadcrumb lives here */}
        <div className="hidden md:block">
          <Navbar breadcrumbs={breadcrumbs} rightContent={rightContent} />
        </div>

        {/* Page content — no breadcrumb here */}
        <main className="flex-1 overflow-auto">{children}</main>

        {/* Footer */}
        <footer className="hidden md:flex items-center justify-center gap-2 py-4 bg-white border-t border-gray-200 shrink-0 text-sm text-black font-serif">
          <span>Powered by</span>
          <img src="/assets/logo.svg" alt="NYAI" width={46} height={12} />
        </footer>
      </div>
    </div>
  );
}
